import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import AudioPlayer from "./AudioPlayer";
import ApiService from "../services/api";

const {
  FiX,
  FiLoader,
  FiCheckCircle,
  FiAlertCircle,
  FiUsers,
  FiMic,
  FiEdit,
  FiPlayCircle,
  FiUser,
  FiCopy,
  FiFileText,
  FiCheck,
} = FiIcons;

const SpeakerInput = ({ speaker, number, onChange }) => (
  <div className="space-y-3 p-4 border border-slate-200 rounded-xl bg-white shadow-sm">
    <h4 className="font-bold text-slate-800 flex items-center gap-2">
      <FiUsers className="text-green-600" />
      ข้อมูลผู้พูด {number}
    </h4>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <input type="text" placeholder="ชื่อ" value={speaker.name} onChange={(e) => onChange(number, 'name', e.target.value)} className="w-full p-2.5 bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors" />
      <input type="text" placeholder="บทบาท (เช่น ผู้ดำเนินรายการ)" value={speaker.role} onChange={(e) => onChange(number, 'role', e.target.value)} className="w-full p-2.5 bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors" />
      <select value={speaker.gender} onChange={(e) => onChange(number, 'gender', e.target.value)} className="w-full p-2.5 bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors appearance-none">
        <option>ชาย</option>
        <option>หญิง</option>
      </select>
      <input type="text" placeholder="ลักษณะน้ำเสียง (เช่น สดใส)" value={speaker.tone} onChange={(e) => onChange(number, 'tone', e.target.value)} className="w-full p-2.5 bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors" />
    </div>
  </div>
);

const PodcastModal = ({ isOpen, onClose, transcript }) => {
  const [numSpeakers, setNumSpeakers] = useState(2);
  const [speaker1, setSpeaker1] = useState({ name: "ผู้พูด 1", role: "", gender: "ชาย", tone: "เป็นมิตร" });
  const [speaker2, setSpeaker2] = useState({ name: "ผู้พูด 2", role: "", gender: "หญิง", tone: "มืออาชีพ" });
  const [prompt, setPrompt] = useState("");
  const [jobId, setJobId] = useState(null);
  const [generatedScript, setGeneratedScript] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [generationStatus, setGenerationStatus] = useState({
    generating: false,
    success: false,
    error: null,
    audioData: null,
  });
  const [currentStatus, setCurrentStatus] = useState('pending');
  const pollingInterval = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setJobId(null);
      setCurrentStatus('pending');
      setGenerationStatus({ generating: false, success: false, error: null, audioData: null });
      setPrompt("");
      setGeneratedScript("");
      setIsCopied(false);
    } else {
      // Cleanup on close
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    }
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (jobId && generationStatus.generating) {
      pollingInterval.current = setInterval(async () => {
        try {
          const statusResult = await ApiService.getPodcastStatus(jobId);
          setCurrentStatus(statusResult.status); // Update current status

          if (statusResult.status === 'completed') {
            clearInterval(pollingInterval.current);
            setGeneratedScript(statusResult.generatedScript);
            setGenerationStatus({ generating: false, success: true, error: null, audioData: { data: statusResult.audioData, mimeType: statusResult.mimeType } });
          } else if (statusResult.status === 'failed') {
            clearInterval(pollingInterval.current);
            setGenerationStatus({ generating: false, success: false, error: statusResult.errorMessage || "Failed to generate podcast.", audioData: null });
          }
        } catch (error) {
          clearInterval(pollingInterval.current);
          setGenerationStatus({ generating: false, success: false, error: "Could not retrieve podcast status.", audioData: null });
        }
      }, 5000); // Poll every 5 seconds
    }
  }, [jobId, generationStatus.generating]);

  const handleGenerate = async () => {
    setGenerationStatus({ generating: true, success: false, error: null, audioData: null });
    setCurrentStatus('pending');
    const speakers = [speaker1];
    if (numSpeakers === 2) {
      speakers.push(speaker2);
    }
    try {
      const response = await ApiService.generatePodcast({ transcript, speakers, prompt });
      if (response.jobId) {
        setJobId(response.jobId);
      } else {
        throw new Error("Did not receive a job ID from the server.");
      }
    } catch (error) {
      console.error("Failed to start podcast generation:", error);
      setGenerationStatus({ generating: false, success: false, error: "Failed to start podcast generation process.", audioData: null });
    }
  };
  
  const handleSpeakerChange = (speaker, field, value) => {
    if (speaker === 1) {
      setSpeaker1(prev => ({ ...prev, [field]: value }));
    } else {
      setSpeaker2(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleCopyScript = () => {
    navigator.clipboard.writeText(generatedScript).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  const getLoadingMessage = () => {
    switch (currentStatus) {
      case 'script_generation':
        return 'กำลังสร้างบทสนทนา...';
      case 'speech_generation':
        return 'กำลังสร้างไฟล์เสียง...';
      case 'pending':
      case 'processing':
      default:
        return 'กำลังสร้าง Podcast...';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          className="bg-slate-50 rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]"
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
        >
          <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-white rounded-t-xl">
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-3">
              <span className="p-2 bg-green-100 rounded-lg">
                <FiMic className="text-green-600" />
              </span>
              สร้าง Podcast สรุปการประชุม
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
            >
              <FiX size={20} />
            </button>
          </div>

          <div className="p-5 overflow-y-auto flex-1 space-y-5 bg-slate-50">
            {generationStatus.generating ? (
              <div className="text-center p-6">
                <FiLoader className="animate-spin text-green-600 w-12 h-12 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-800">{getLoadingMessage()}</h3>
                <p className="text-slate-500 mt-2">ขั้นตอนนี้อาจใช้เวลาหลายนาที กรุณารอสักครู่</p>
              </div>
            ) : generationStatus.success && generationStatus.audioData ? (
                <div className="space-y-4">
                    <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                      <h3 className="text-xl font-bold text-green-600 mb-4">Podcast ของคุณพร้อมแล้ว!</h3>
                      <AudioPlayer audioData={generationStatus.audioData} />
                    </div>
                    <div className="p-4 bg-white rounded-xl shadow-sm">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-bold text-slate-800 flex items-center gap-2">
                          <FiFileText className="text-green-600"/>
                          บทสนทนาที่สร้างขึ้น
                        </h4>
                        <button onClick={handleCopyScript} className="px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-100 rounded-md hover:bg-green-200 transition-colors flex items-center gap-1.5">
                          <SafeIcon icon={isCopied ? FiCheck : FiCopy} />
                          {isCopied ? 'คัดลอกแล้ว' : 'คัดลอก'}
                        </button>
                      </div>
                      <textarea
                        readOnly
                        value={generatedScript}
                        className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 h-40 resize-none"
                      />
                    </div>
                </div>
            ) : (
              <>
                <div className="p-4 border border-slate-200 rounded-xl bg-white shadow-sm">
                  <h4 className="font-bold text-slate-800 flex items-center gap-2 mb-3">
                    <FiUsers className="text-green-600" />
                    จำนวนผู้พูด
                  </h4>
                  <div className="flex gap-2">
                    {[1, 2].map(num => (
                      <button
                        key={num}
                        onClick={() => setNumSpeakers(num)}
                        className={`flex-1 p-3 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${
                          numSpeakers === num
                            ? 'bg-green-600 text-white shadow-md'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        <FiUser />
                        {num} คน
                      </button>
                    ))}
                  </div>
                </div>

                <SpeakerInput speaker={speaker1} number={1} onChange={handleSpeakerChange} />
                {numSpeakers === 2 && (
                  <SpeakerInput speaker={speaker2} number={2} onChange={handleSpeakerChange} />
                )}
                
                <div className="p-4 border border-slate-200 rounded-xl bg-white shadow-sm">
                   <h4 className="font-bold text-slate-800 flex items-center gap-2 mb-3">
                      <FiEdit className="text-green-600" />
                      คำสั่งเพิ่มเติม (ไม่จำเป็น)
                    </h4>
                  <textarea
                    id="prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="w-full p-2.5 bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                    rows="3"
                    placeholder="เช่น ทำให้บทสนทนาดูเป็นกันเองเหมือนเพื่อนคุยกัน"
                  />
                </div>
              </>
            )}
             {generationStatus.error && (
              <div className="p-3 mt-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-2">
                <SafeIcon icon={FiAlertCircle} />
                <p className="text-sm">{generationStatus.error}</p>
              </div>
            )}
          </div>

          <div className="p-5 border-t border-slate-200 flex justify-end gap-3 bg-slate-50/50 rounded-b-xl">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 transition-all shadow-sm"
            >
              ยกเลิก
            </button>
            {!generationStatus.success && (
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generationStatus.generating}
                className="px-5 py-2.5 text-sm font-bold text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-2 shadow-lg shadow-green-500/20 hover:shadow-xl hover:shadow-green-500/30 transition-all disabled:bg-slate-400 disabled:shadow-none"
              >
                {generationStatus.generating ? (
                  <><FiLoader className="animate-spin" /> {getLoadingMessage()}</>
                ) : (
                  <><FiPlayCircle /> เริ่มสร้าง Podcast</>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PodcastModal;
